import prepareTranslate from '@/helpers/dictionary';
import { useForm } from '@mantine/form';
import {
  Button,
  Grid,
  NumberInput,
  Select,
  TextInput,
} from '@mantine/core';
import { useState } from 'react';
import { ACCORD_TYPE, PRODUCT_ENTITY } from '@/helpers/enums';
import DetailsModal from '@/components/DetailsModal';
import companyQuery from '@/api/queries/company';
import productQuery from '@/api/queries/product';
import validations from '@/helpers/validation';
import Modal from '@/components/Modal/Index';
import { useDisclosure } from '@mantine/hooks';
import PropTypes from 'prop-types';
import agentMutate from '@/api/mutitations/agent';

const AgentAlternativeFundCreate = (props) => {
  const {
    open,
    close,
    opened,
    setKey,
    id,
  } = props;

  const t = prepareTranslate();
  const [actionResultOpended,
    { open: actionResultOpen, close: actionResultClose },
  ] = useDisclosure();

  const { data: companiesData } = companyQuery.useGetAll();
  const { data: productsData } = productQuery.useGetAll();
  const [loading, setLoading] = useState(false);
  const [resultLabel, setResultLabel] = useState('');

  const form = useForm({
    initialValues: {
      accordType: 'independent',
    },
    validate: {
      companyId: (value) => validations.required(value),
      accordType: (value) => validations.required(value),
      branchId: (value) => validations.required(value),
      alternativeFundNumber: (value) => validations.required(value),
    },
  });

  const companiesOptions = companiesData ? (
    companiesData.map((company) => ({
      label: company.hebrew,
      value: company._id,
    }))) : (
    []
  );

  // eslint-disable-next-line max-len
  const productsOptions = productsData?.filter((product) => product.applicableTo.includes(PRODUCT_ENTITY.alternativeFund)).map((product) => ({
    label: product.hebrew,
    value: product._id,
  }));

  const { mutateAsync: addAlternativeFundMutate } = agentMutate.usePushToArray();

  const handleAlternativeFundSave = async (values) => {
    setLoading(true);

    try {
      const clonedValues = { ...values, status: 'active' };

      const data = {
        value: { ...clonedValues },
        id,
        key: 'alternativeFunds',
      };
      const newAlternativeFund = await addAlternativeFundMutate(data);

      if (!newAlternativeFund) {
        setResultLabel(t('pages.agentEdit.notification.failure.title'));
        actionResultOpen();
        return;
      }

      setResultLabel(t('components.createAlternativeFunds.success'));
      actionResultOpen();
      form.reset();
      setKey((prev) => prev + 1);
      close();
    } catch (error) {
      setResultLabel(t('pages.agentEdit.notification.failure.title'));
      actionResultOpen();
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    form.reset();
    form.clearErrors();
    close();
  };

  const actions = [
    <Button
      key='primary'
      loading={loading}
      onClick={form.onSubmit(handleAlternativeFundSave)}
      w={'7rem'}
    >
      {t('global.update')}
    </Button>,
    <Button
      key='cancel'
      variant='outline'
      onClick={handleCancelClick}
      w={'7rem'}>
      {t('global.cancel')}
    </Button>,
  ];

  const resultActions = [
    <Button
      key='clseButton' onClick={() => actionResultClose()}>
      {t('global.close')}
    </Button>,
  ];

  const alternativeFundAccordType = form.getValues().accordType;

  return (
    <>
      <DetailsModal
        actions={actions}
        open={open}
        close={handleCancelClick}
        opened={opened}
        label={t('components.editAlternativeFunds.addAlternativeFund')}
      >
        <Grid gutter={{ base: 'xl' }}>
          <Grid.Col span={{ base: 3 }}>
            <Select
              label={t('components.editAlternativeFunds.company')}
              key={form.key('companyId')}
              data={companiesOptions}
              required
              {...form.getInputProps('companyId')}
            />
            <TextInput
              label={t('components.editAlternativeFunds.alternativeFundType')}
              key={form.key('alternativeFundType')}
              {...form.getInputProps('alternativeFundType')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 3 }}>
            <Select
              required
              label={t('components.editAlternativeFunds.branch')}
              data={productsOptions}
              key={form.key('branchId')}
              {...form.getInputProps('branchId')}
            />
                 <Select
              required
              label={t('components.editAlternativeFunds.accordType')}
              key={form.key('accordType')}
              defaultValue={ACCORD_TYPE[1].value}
              data={ACCORD_TYPE}
              {...form.getInputProps('accordType')}
            />

          </Grid.Col>
          <Grid.Col span={{ base: 3 }}>
            <TextInput
              autoComplete='off'
              label={t('components.editAlternativeFunds.number')}
              key={form.key('alternativeFundNumber')}
              required
              {...form.getInputProps('alternativeFundNumber')}
            />
           {
              alternativeFundAccordType === 'independent' ? (
                <NumberInput
                  autoComplete='off'
                  label={t('components.editAlternativeFunds.commission')}
                  key={form.key('agentCommission')}
                  maxLength={7}
                  {...form.getInputProps('agentCommission')}
                />
              ) : (
                <NumberInput
                  autoComplete='off'
                  label={t('components.editAlternativeFunds.commission')}
                  key={form.key('employeeAgentCommission')}
                  maxLength={7}
                  {...form.getInputProps('employeeAgentCommission')}
                />
              )
            }
          </Grid.Col>
          <Grid.Col span={{ base: 3 }}>
            <TextInput
              label={t('components.editAlternativeFunds.alternativeFundName')}
              key={form.key('alternativeFundName')}
              {...form.getInputProps('alternativeFundName')}
            />
          </Grid.Col>
        </Grid>
        <Grid>
          <Grid.Col>
            <TextInput
              label={t('components.editAlternativeFunds.comments')}
              key={form.key('comments')}
              maxLength={300}
              {...form.getInputProps('comments')}
            />
          </Grid.Col>
        </Grid>
      </DetailsModal>
      <Modal
        opened={actionResultOpended}
        onClose={actionResultClose}
        actions={resultActions}
        label={resultLabel}
      />
    </>
  );
};

AgentAlternativeFundCreate.propTypes = {
  open: PropTypes.func,
  close: PropTypes.func,
  opened: PropTypes.bool,
  setKey: PropTypes.func,
  id: PropTypes.string,
};

export default AgentAlternativeFundCreate;
